import { useMemo } from 'react';
import { NavLink, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { PanelRightOpen } from 'lucide-react';
import { cn } from '@/lib/utils';
import { isNavPathActive, mostSpecificNavPath, ownerPath } from '@/lib/hosts';
import { useSubNav } from '@/contexts/SubNavContext';
import { usePermissions } from '@/hooks/usePermissions';
import { useCurrentShopDetail } from '@/hooks/queries/useCurrentShopDetail';
import type { NavChild } from '@/lib/navigation';
import { rentalWorkspaceLabel } from '@/lib/rental-labels';

/**
 * Persistent sub-nav panel — HeyGen-style.
 *
 * Renders as a fixed 256px panel positioned right of the 88px rail, full
 * viewport height. Only visible when `useSubNav().state === 'open'`. Replaces
 * the hover-based FlyoutPanel.
 *
 * The collapse button (top-right) flips the state to `'collapsed'`, at which
 * point `<RailSubnavCollapsedPill>` takes over in the main content top-left.
 */
export function RailSubnavPanel() {
  const { state, activeItem, collapse } = useSubNav();
  const { pathname } = useLocation();
  const { t } = useTranslation(['navigation']);
  const { can } = usePermissions();
  const { data: shopDetailResponse } = useCurrentShopDetail();
  const shopDetail = shopDetailResponse?.data;

  const shopFlags = useMemo(
    () => ({
      drivers_enabled: Boolean(shopDetail?.drivers_enabled),
      returns_enabled: Boolean(shopDetail?.returns_enabled),
      bar_mode_enabled: Boolean(shopDetail?.bar_mode_enabled),
      ingredient_management_enabled: Boolean(shopDetail?.ingredient_management_enabled),
      partner_mode_enabled: Boolean(shopDetail?.partner_mode_enabled),
      commerce_pages_enabled: Boolean(shopDetail?.commerce_pages_enabled),
      tracked_stock_transfers_v1: Boolean(shopDetail?.tracked_stock_transfers_v1),
      rental_mode_enabled: Boolean(shopDetail?.rental_mode_enabled),
    }),
    [
      shopDetail?.drivers_enabled,
      shopDetail?.returns_enabled,
      shopDetail?.bar_mode_enabled,
      shopDetail?.ingredient_management_enabled,
      shopDetail?.partner_mode_enabled,
      shopDetail?.commerce_pages_enabled,
      shopDetail?.tracked_stock_transfers_v1,
      shopDetail?.rental_mode_enabled,
    ],
  );

  const visibleChildren: NavChild[] = useMemo(() => {
    if (!activeItem?.children) return [];
    return activeItem.children.filter((c) => {
      if (c.permission && !can(c.permission)) return false;
      if (c.featureFlag && !shopFlags[c.featureFlag]) return false;
      return true;
    });
  }, [activeItem, can, shopFlags]);

  const showPanel = state === 'open' && activeItem && visibleChildren.length > 0;
  const activeChildPath = mostSpecificNavPath(
    pathname,
    visibleChildren.map((child) => ownerPath(child.href)),
  );
  const activeLabel =
    activeItem?.labelKey === 'rental'
      ? rentalWorkspaceLabel({
          category: shopDetail?.category as string | null | undefined,
          profile: shopDetail?.rental_profile as string | null | undefined,
        })
      : activeItem
        ? t(`navigation:${activeItem.labelKey}`)
        : '';

  // Always render a flex-flow spacer so the main content stays beside the fixed
  // panel (never under it). Width transitions between 0 and 16rem (256px) to
  // mirror the slide-in animation, matching the rail's permanent 88px spacer.
  const spacer = (
    <div
      aria-hidden="true"
      className={cn(
        'hidden shrink-0 lg:block',
        'transition-[width] duration-200 ease-[cubic-bezier(0.16,1,0.3,1)]',
        showPanel ? 'lg:w-64' : 'lg:w-0',
      )}
    />
  );

  if (!showPanel) {
    return spacer;
  }

  const ActiveIcon = activeItem.icon;

  return (
    <>
      {spacer}
      <aside
        role="navigation"
        aria-label={activeLabel}
        className={cn(
          'fixed left-[88px] top-0 z-30 hidden h-screen w-64 flex-col',
          'border-r border-slate-200 bg-white px-3 py-4',
          'shadow-[0_8px_24px_-12px_rgba(15,23,42,0.10)]',
          'animate-in slide-in-from-left-4 fade-in-0 duration-200',
          'dark:border-charcoal-700 dark:bg-charcoal-900',
          'lg:flex',
        )}
      >
        {/* Header — icon + title + collapse button */}
        <header className="mb-3 flex items-center gap-2.5 border-b border-slate-200 pb-3 dark:border-charcoal-700">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-lime-100 text-lime-900 dark:bg-lime-500/15 dark:text-lime-300">
            <ActiveIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" />
          </div>
          <div className="min-w-0 flex-1 leading-tight">
            <p className="truncate text-[14px] font-semibold text-charcoal-950 dark:text-slate-50">
              {activeLabel}
            </p>
            <p className="font-mono text-[10px] text-slate-600 dark:text-slate-300">
              {visibleChildren.length}{' '}
              {t('navigation:sub_sections', { defaultValue: 'sous-sections' })}
            </p>
          </div>
          <button
            type="button"
            onClick={collapse}
            aria-label={t('navigation:collapse_subnav', { defaultValue: 'Réduire' })}
            className="flex h-7 w-7 items-center justify-center rounded-md text-slate-500 transition-colors hover:bg-slate-100 hover:text-charcoal-950 dark:text-slate-400 dark:hover:bg-charcoal-800 dark:hover:text-slate-50"
          >
            <PanelRightOpen className="h-3.5 w-3.5" strokeWidth={1.75} />
          </button>
        </header>

        {/* Children list */}
        <ul className="flex flex-col gap-0.5">
          {visibleChildren.map((child) => {
            const ChildIcon = child.icon;
            const target = ownerPath(child.href);
            const isActive = target === activeChildPath && isNavPathActive(pathname, target);
            return (
              <li key={child.href}>
                <NavLink
                  to={target}
                  aria-current={isActive ? 'page' : undefined}
                  className={({ isPending }) =>
                    cn(
                      'flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13px] font-medium transition-colors',
                      'text-slate-600 hover:bg-slate-100 hover:text-charcoal-950 dark:text-slate-300 dark:hover:bg-charcoal-800 dark:hover:text-slate-50',
                      isActive &&
                        'bg-lime-50 text-charcoal-950 dark:bg-lime-500/12 dark:text-lime-300',
                      isPending && 'animate-pulse bg-lime-50/70 text-charcoal-950',
                    )
                  }
                >
                  <span
                    className={cn(
                      'flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-md bg-slate-100 text-slate-500 dark:bg-charcoal-800 dark:text-slate-400',
                      isActive &&
                        'bg-lime-100 text-lime-700 dark:bg-lime-500/20 dark:text-lime-300',
                    )}
                  >
                    {ChildIcon ? (
                      <ChildIcon className="h-3 w-3" strokeWidth={2} aria-hidden="true" />
                    ) : null}
                  </span>
                  <span className="flex-1 truncate">{t(`navigation:${child.labelKey}`)}</span>
                </NavLink>
              </li>
            );
          })}
        </ul>
      </aside>
    </>
  );
}
